/**
 * Unified tree renderer for both file and component trees
 * Eliminates ~600 lines of duplicated code in app.ts
 */

import type { TreeNode, InlineInputState } from './types.js';
import { buildFileTree, findNode, getFilesUnderFolder, type PathItem } from './TreeBuilder.js';
import { DragDropHandler, type OnDropCallback } from './DragDropHandler.js';
import { createInlineInput, createInlineRename } from './InlineInput.js';
import { showContextMenu } from './ContextMenu.js';

/**
 * Configuration for creating a TreeRenderer instance
 */
export interface TreeRendererConfig {
  /** The container element to render the tree into */
  container: HTMLElement;
  /** File extension to strip from display names (e.g., '.src.md' or '.md') */
  fileExtension: string;
  /** Type identifier for icons and styling */
  treeType: 'file' | 'component';
  /** Callback when a file is selected */
  onSelect: (path: string) => Promise<void>;
  /** Callback when a file/folder is dropped (move operation) */
  onDrop: OnDropCallback;
  /** Callback for context menu actions */
  onContextAction: (action: string, path: string | null, type: 'file' | 'folder' | 'empty') => void;
  /** Callback when inline create is confirmed */
  onInlineCreate: (name: string, type: 'file' | 'folder', parentPath: string) => Promise<void>;
  /** Callback when inline rename is confirmed */
  onInlineRename: (node: TreeNode, newName: string) => Promise<void>;
  /** Callback to get the currently selected file path */
  getCurrentFile: () => string | null;
  /** Optional path prefix to add when checking current file (e.g., '_components/') */
  pathPrefix?: string;
}

/**
 * Icons for file tree rendering
 */
const ICONS = {
  folder: `<svg class="tree-folder-icon" xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h240l80 80h320q33 0 56.5 23.5T880-640v400q0 33-23.5 56.5T800-160H160Z"/></svg>`,
  toggle: `<svg xmlns="http://www.w3.org/2000/svg" height="12" viewBox="0 -960 960 960" width="12" fill="currentColor"><path d="M480-345 240-585l56-56 184 184 184-184 56 56-240 240Z"/></svg>`,
  file: `<svg class="tree-file-icon" xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M320-240h320v-80H320v80Zm0-160h320v-80H320v80ZM240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v480q0 33-23.5 56.5T720-80H240Z"/></svg>`,
  component: `<svg class="tree-file-icon component-icon" xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M352-120H200q-33 0-56.5-23.5T120-200v-152q48 0 84-30.5t36-77.5q0-47-36-77.5T120-568v-152q0-33 23.5-56.5T200-800h160q0-42 29-71t71-29q42 0 71 29t29 71h160q33 0 56.5 23.5T800-720v160q42 0 71 29t29 71q0 42-29 71t-71 29v160q0 33-23.5 56.5T720-120H568q0-50-31.5-85T460-240q-45 0-76.5 35T352-120Z"/></svg>`,
  readme: `<svg class="tree-file-icon readme-icon" xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M240-200h480v-80H240v80Zm0-160h480v-80H240v80Zm0-160h240v-80H240v80ZM160-80q-33 0-56.5-23.5T80-160v-640q0-33 23.5-56.5T160-880h640q33 0 56.5 23.5T880-800v640q0 33-23.5 56.5T800-80H160Z"/></svg>`,
  rootFolder: `<svg xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h240l80 80h320q33 0 56.5 23.5T880-640v400q0 33-23.5 56.5T800-160H160Z"/></svg>`,
  rootComponent: `<svg xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M352-120H200q-33 0-56.5-23.5T120-200v-152q48 0 84-30.5t36-77.5q0-47-36-77.5T120-568v-152q0-33 23.5-56.5T200-800h160q0-42 29-71t71-29q42 0 71 29t29 71h160q33 0 56.5 23.5T800-720v160q42 0 71 29t29 71q0 42-29 71t-71 29v160q0 33-23.5 56.5T720-120H568q0-50-31.5-85T460-240q-45 0-76.5 35T352-120Z"/></svg>`,
};

/**
 * Unified tree renderer that handles both file and component trees
 */
export class TreeRenderer {
  private config: TreeRendererConfig;
  private nodes: TreeNode[] = [];
  private items: PathItem[] = [];
  private expandedFolders: Set<string> = new Set();
  private pendingFolders: Set<string> = new Set();
  private dragDropHandler: DragDropHandler;
  private inlineCreateState: InlineInputState | null = null;
  private inlineRenameState: { path: string; type: 'file' | 'folder' } | null = null;

  constructor(config: TreeRendererConfig) {
    this.config = config;
    this.dragDropHandler = new DragDropHandler(config.onDrop);
    this.setupContainerEvents();
  }

  /**
   * Get expanded folders set (for external management)
   */
  get expanded(): Set<string> {
    return this.expandedFolders;
  }

  /**
   * Get pending folders set (for external management)
   */
  get pending(): Set<string> {
    return this.pendingFolders;
  }

  /**
   * Get the current tree nodes
   */
  getNodes(): TreeNode[] {
    return this.nodes;
  }

  /**
   * Expand folders to show a specific file path
   */
  expandToPath(filePath: string): void {
    const parts = filePath.split('/');
    parts.pop(); // Remove filename
    let currentPath = '';
    for (const part of parts) {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      this.expandedFolders.add(currentPath);
    }
  }

  /**
   * Render the tree with given items
   */
  render(items: PathItem[]): void {
    this.items = items;
    this.nodes = buildFileTree(items, this.expandedFolders);

    // Add pending folders (empty folders being created)
    this.addPendingFolders();

    // Clean up pending folders that now have real files
    this.cleanupPendingFolders();

    // Clear and render
    this.config.container.innerHTML = '';

    // Add root drop zone
    const rootDropZone = this.createRootDropZone();
    this.config.container.appendChild(rootDropZone);

    // Add inline input for root-level creation if active
    if (this.inlineCreateState?.active && this.inlineCreateState.parentPath === '') {
      const inlineInput = createInlineInput({
        type: this.inlineCreateState.type,
        parentPath: '',
        depth: 0,
        onConfirm: (name) => this.handleInlineCreateConfirm(name),
        onCancel: () => this.cancelInlineCreate(),
      });
      this.config.container.appendChild(inlineInput);
    }

    // Render tree nodes
    const fragment = this.renderNodes(this.nodes, 0);
    this.config.container.appendChild(fragment);
  }

  /**
   * Start inline creation mode
   */
  startInlineCreate(type: 'file' | 'folder', parentPath: string): void {
    if (parentPath) {
      this.expandedFolders.add(parentPath);
    }
    this.inlineCreateState = { active: true, type, parentPath };
    this.render(this.items);
  }

  /**
   * Cancel inline creation
   */
  cancelInlineCreate(): void {
    this.inlineCreateState = null;
    this.render(this.items);
  }

  /**
   * Start inline rename mode
   */
  startInlineRename(path: string, type: 'file' | 'folder'): void {
    this.inlineRenameState = { path, type };
    this.render(this.items);
  }

  /**
   * Cancel inline rename
   */
  cancelInlineRename(): void {
    this.inlineRenameState = null;
    this.render(this.items);
  }

  /**
   * Toggle folder expanded state
   */
  toggleFolder(folderPath: string): void {
    if (this.expandedFolders.has(folderPath)) {
      this.expandedFolders.delete(folderPath);
    } else {
      this.expandedFolders.add(folderPath);
    }
    this.render(this.items);
  }

  /**
   * Refresh the active file selection styling
   */
  refreshSelection(): void {
    this.config.container.querySelectorAll('.tree-file').forEach(el =>
      el.classList.remove('active')
    );

    const currentFile = this.config.getCurrentFile();
    if (currentFile) {
      // Handle path prefix for components
      let matchPath = currentFile;
      if (this.config.pathPrefix && currentFile.startsWith(this.config.pathPrefix)) {
        matchPath = currentFile.substring(this.config.pathPrefix.length);
      }

      const fileEl = this.config.container.querySelector(`[data-path="${matchPath}"]`);
      fileEl?.classList.add('active');
    }
  }

  /**
   * Filter visible items by search term
   */
  filter(searchTerm: string): void {
    const term = searchTerm.toLowerCase().trim();

    if (!term) {
      // Show all, restore collapsed state
      this.config.container.querySelectorAll('.tree-folder, .tree-file').forEach(el => {
        el.classList.remove('search-hidden');
      });
      this.config.container.querySelectorAll('.tree-folder').forEach(folder => {
        const path = (folder as HTMLElement).dataset.path || '';
        if (!this.expandedFolders.has(path)) {
          folder.classList.add('collapsed');
        } else {
          folder.classList.remove('collapsed');
        }
      });
      return;
    }

    // Filter folders and files
    this.config.container.querySelectorAll('.tree-folder').forEach(folder => {
      const folderPath = ((folder as HTMLElement).dataset.path || '').toLowerCase();
      const folderName = (folder.querySelector('.tree-folder-name')?.textContent || '').toLowerCase();
      let hasVisibleFiles = false;

      folder.querySelectorAll('.tree-file').forEach(file => {
        const filePath = ((file as HTMLElement).dataset.path || '').toLowerCase();
        const displayName = (file.querySelector('.tree-file-name')?.textContent || '').toLowerCase();

        if (filePath.includes(term) || displayName.includes(term) || folderPath.includes(term)) {
          file.classList.remove('search-hidden');
          hasVisibleFiles = true;
        } else {
          file.classList.add('search-hidden');
        }
      });

      if (hasVisibleFiles || folderName.includes(term) || folderPath.includes(term)) {
        folder.classList.remove('search-hidden');
        folder.classList.remove('collapsed');
      } else {
        folder.classList.add('search-hidden');
      }
    });

    // Filter root-level files
    this.config.container.querySelectorAll(':scope > .tree-file').forEach(file => {
      const filePath = ((file as HTMLElement).dataset.path || '').toLowerCase();
      const displayName = (file.querySelector('.tree-file-name')?.textContent || '').toLowerCase();

      if (filePath.includes(term) || displayName.includes(term)) {
        file.classList.remove('search-hidden');
      } else {
        file.classList.add('search-hidden');
      }
    });
  }

  /**
   * Get files under a folder path
   */
  getFilesUnderFolder(folderPath: string): string[] {
    return getFilesUnderFolder(this.nodes, folderPath);
  }

  // --- Private methods ---

  private setupContainerEvents(): void {
    // Context menu
    this.config.container.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      const target = e.target as HTMLElement;
      const fileItem = target.closest('.tree-file') as HTMLElement | null;
      const folderItem = target.closest('.tree-folder') as HTMLElement | null;

      let targetPath: string | null = null;
      let targetType: 'file' | 'folder' | 'empty' = 'empty';

      if (fileItem) {
        targetPath = fileItem.dataset.path ?? null;
        targetType = 'file';
      } else if (folderItem) {
        targetPath = folderItem.dataset.path ?? null;
        targetType = 'folder';
      }

      showContextMenu(
        e.clientX,
        e.clientY,
        targetPath,
        targetType,
        (action) => this.config.onContextAction(action, targetPath, targetType),
        () => {}
      );
    });

    // Container-level drag handlers for root-level drops
    const isEmptySpaceTarget = (target: EventTarget | null): boolean => {
      if (!target || !(target instanceof HTMLElement)) return false;
      return !target.closest('.tree-file, .tree-folder');
    };

    this.config.container.addEventListener('dragover', (e) => {
      if (isEmptySpaceTarget(e.target)) {
        e.preventDefault();
        this.dragDropHandler.handleDragOver(e, null);
      }
    });

    this.config.container.addEventListener('dragenter', (e) => {
      if (isEmptySpaceTarget(e.target)) {
        this.dragDropHandler.handleDragEnter(e, this.config.container, null);
      }
    });

    this.config.container.addEventListener('dragleave', (e) => {
      if (isEmptySpaceTarget(e.target)) {
        this.dragDropHandler.handleDragLeave(e, this.config.container);
      }
    });

    this.config.container.addEventListener('drop', (e) => {
      if (isEmptySpaceTarget(e.target)) {
        e.preventDefault();
        this.dragDropHandler.handleDrop(e, null);
      }
    });
  }

  private addPendingFolders(): void {
    const foldersToAdd = new Set(this.pendingFolders);
    if (this.inlineCreateState?.active && this.inlineCreateState.parentPath) {
      foldersToAdd.add(this.inlineCreateState.parentPath);
    }

    for (const folderPath of foldersToAdd) {
      const existingFolder = findNode(this.nodes, folderPath);
      if (!existingFolder) {
        const pathParts = folderPath.split('/');
        let currentNodes = this.nodes;
        let currentPath = '';

        for (let i = 0; i < pathParts.length; i++) {
          const folderName = pathParts[i];
          currentPath = currentPath ? `${currentPath}/${folderName}` : folderName;

          let folder = currentNodes.find(n => n.path === currentPath && n.type === 'folder');
          if (!folder) {
            folder = {
              id: currentPath,
              name: folderName,
              type: 'folder',
              path: currentPath,
              depth: i,
              children: [],
              isExpanded: true,
              isPending: true,
            };
            currentNodes.push(folder);
            this.sortNodes(currentNodes);
            this.expandedFolders.add(currentPath);
          }
          currentNodes = folder.children!;
        }
      }
    }
  }

  private cleanupPendingFolders(): void {
    for (const folderPath of this.pendingFolders) {
      const hasFiles = this.items.some(f => f.path.startsWith(folderPath + '/'));
      if (hasFiles) {
        this.pendingFolders.delete(folderPath);
      }
    }
  }

  private sortNodes(nodes: TreeNode[]): void {
    nodes.sort((a, b) => {
      if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }

  private createRootDropZone(): HTMLElement {
    const rootDropZone = document.createElement('div');
    rootDropZone.className = 'tree-root-dropzone';

    const icon = this.config.treeType === 'component' ? ICONS.rootComponent : ICONS.rootFolder;
    rootDropZone.innerHTML = `${icon}<span>/</span>`;

    // Attach drag handlers
    rootDropZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.dragDropHandler.handleDragOver(e, null);
    });
    rootDropZone.addEventListener('dragenter', (e) => {
      e.stopPropagation();
      this.dragDropHandler.handleDragEnter(e, rootDropZone, null);
    });
    rootDropZone.addEventListener('dragleave', (e) => {
      e.stopPropagation();
      this.dragDropHandler.handleDragLeave(e, rootDropZone);
    });
    rootDropZone.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.dragDropHandler.handleDrop(e, null);
    });

    return rootDropZone;
  }

  private renderNodes(nodes: TreeNode[], depth: number): DocumentFragment {
    const fragment = document.createDocumentFragment();

    for (const node of nodes) {
      // Check if this node is being renamed inline
      if (this.inlineRenameState?.path === node.path) {
        const renameInput = createInlineRename(
          node.name,
          depth,
          node.type,
          (newName) => this.handleInlineRenameConfirm(node, newName),
          () => this.cancelInlineRename()
        );
        fragment.appendChild(renameInput);
        continue;
      }

      const element = node.type === 'folder'
        ? this.renderFolderNode(node, depth)
        : this.renderFileNode(node, depth);
      fragment.appendChild(element);
    }

    return fragment;
  }

  private renderFolderNode(node: TreeNode, depth: number): HTMLElement {
    const folder = document.createElement('div');
    const isExpanded = this.expandedFolders.has(node.path);
    folder.className = `tree-folder${isExpanded ? '' : ' collapsed'}${node.isPending ? ' pending' : ''}`;
    folder.dataset.path = node.path;
    folder.dataset.type = 'folder';

    const indent = depth * 16;

    // Create header
    const header = document.createElement('div');
    header.className = 'tree-folder-header';
    header.style.paddingLeft = `${12 + indent}px`;
    header.draggable = !node.isPending;
    header.innerHTML = `
      <span class="tree-toggle">${ICONS.toggle}</span>
      ${ICONS.folder}
      <span class="tree-folder-name">${node.name}</span>
    `;
    folder.appendChild(header);

    // Create children container
    const childrenContainer = document.createElement('div');
    childrenContainer.className = 'tree-folder-children';

    // Add inline input for this folder if active
    if (this.inlineCreateState?.active && this.inlineCreateState.parentPath === node.path) {
      const inlineInput = createInlineInput({
        type: this.inlineCreateState.type,
        parentPath: node.path,
        depth: depth + 1,
        onConfirm: (name) => this.handleInlineCreateConfirm(name),
        onCancel: () => this.cancelInlineCreate(),
      });
      childrenContainer.appendChild(inlineInput);
    }

    // Render children
    if (node.children && node.children.length > 0) {
      childrenContainer.appendChild(this.renderNodes(node.children, depth + 1));
    }

    folder.appendChild(childrenContainer);

    // Click to expand/collapse
    header.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.tree-action')) return;
      this.toggleFolder(node.path);
    });

    // Drag and drop handlers
    if (!node.isPending) {
      header.addEventListener('dragstart', (e) => {
        e.stopPropagation();
        this.dragDropHandler.handleDragStart(e, node);
      });
      header.addEventListener('dragend', (e) => this.dragDropHandler.handleDragEnd(e));
    }

    // Drop target handlers on header
    header.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.dragDropHandler.handleDragOver(e, node);
    });
    header.addEventListener('dragenter', (e) => {
      e.stopPropagation();
      this.dragDropHandler.handleDragEnter(e, header, node);
    });
    header.addEventListener('dragleave', (e) => {
      e.stopPropagation();
      this.dragDropHandler.handleDragLeave(e, header);
    });
    header.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.dragDropHandler.handleDrop(e, node);
    });

    return folder;
  }

  private renderFileNode(node: TreeNode, depth: number): HTMLElement {
    const file = document.createElement('div');
    const currentFile = this.config.getCurrentFile();

    // Determine if active
    let isActive = false;
    if (currentFile) {
      if (this.config.pathPrefix) {
        isActive = currentFile === `${this.config.pathPrefix}${node.path}`;
      } else {
        isActive = currentFile === node.path;
      }
    }

    const indent = depth * 16;

    // README at project root is the home page — gets a distinct icon + class
    const isReadme = depth === 0
      && this.config.treeType === 'file'
      && /^readme$/i.test(node.name.replace(this.config.fileExtension, ''));

    file.className = `tree-file${isActive ? ' active' : ''}${this.config.treeType === 'component' ? ' component-item' : ''}${isReadme ? ' readme-item' : ''}`;
    file.dataset.path = node.path;
    file.dataset.type = 'file';
    file.draggable = true;
    file.style.paddingLeft = `${12 + indent}px`;

    // Display name without extension
    const displayName = node.name.replace(this.config.fileExtension, '');
    const icon = isReadme
      ? ICONS.readme
      : (this.config.treeType === 'component' ? ICONS.component : ICONS.file);

    file.innerHTML = `
      ${icon}
      <span class="tree-file-name">${displayName}</span>
    `;

    // Click to select
    file.addEventListener('click', async (e) => {
      if ((e.target as HTMLElement).closest('.tree-action')) return;
      await this.config.onSelect(node.path);
    });

    // Drag and drop handlers
    file.addEventListener('dragstart', (e) => this.dragDropHandler.handleDragStart(e, node));
    file.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.dragDropHandler.handleDragOver(e, node);
    });
    file.addEventListener('dragenter', (e) => {
      e.stopPropagation();
      this.dragDropHandler.handleDragEnter(e, file, node);
    });
    file.addEventListener('dragleave', (e) => {
      e.stopPropagation();
      this.dragDropHandler.handleDragLeave(e, file);
    });
    file.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.dragDropHandler.handleDrop(e, node);
    });
    file.addEventListener('dragend', (e) => this.dragDropHandler.handleDragEnd(e));

    return file;
  }

  private async handleInlineCreateConfirm(name: string): Promise<void> {
    if (!this.inlineCreateState) return;
    const { type, parentPath } = this.inlineCreateState;

    try {
      await this.config.onInlineCreate(name, type, parentPath);
      this.inlineCreateState = null;
      // Remove from pending if it was a file creation
      if (type === 'file' && parentPath) {
        this.pendingFolders.delete(parentPath);
      }
    } catch (err) {
      console.error('Failed to create:', err);
      this.cancelInlineCreate();
    }
  }

  private async handleInlineRenameConfirm(node: TreeNode, newName: string): Promise<void> {
    try {
      await this.config.onInlineRename(node, newName);
      this.inlineRenameState = null;
    } catch (err) {
      console.error('Failed to rename:', err);
      this.cancelInlineRename();
    }
  }
}
